import CloseIcon from '@mui/icons-material/Close';
import { Dialog, IconButton } from '@mui/material';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import db_config from '../../../configs/db_config';
import { useMenuContext } from '../../../context/MenuContext';
import { useOrderContext } from '../../../context/OrderContext';
import { useSystemContext } from '../../../context/SystemContext/SystemContext';
import usePaymentMethod from '../../../hook/usePaymentMethod';
import { db } from '../../../libs/firebase';
import { calculateOrderItemQuantity, calculateOrderItemsTotal } from '../../../utils';
import { OrderItemStatus, SHIPPING_FEE } from '../../../consts';

declare global {
  interface Window {
    Elepay: any;
  }
}

interface PaymentProps {
  open: boolean;
  onClose: () => void;
  totalPrice: number;
  orderId?: string;
  restaurant_id?: string;
}

const ELEPAY_SCRIPT_URL = 'https://js.elepay.io/v1/elepay.js';
const PAYMENT_DEBUG_FALLBACK_LOG_ID = 'payment_debug_fallback';

const ElepayCheckout: React.FC<{ codeObjectId: string; publicKey?: string }> = ({ codeObjectId, publicKey }) => {
  useEffect(() => {
    if (!publicKey) {
      return;
    }

    const script = document.createElement('script');
    script.src = ELEPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      const elepay = new window.Elepay(publicKey);
      elepay.checkout(codeObjectId);
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [codeObjectId, publicKey]);

  return null;
};

const PAYMENT: React.FC<PaymentProps> = ({ open, onClose, totalPrice, orderId, restaurant_id }) => {
  const { orderDetails } = useOrderContext();
  const { listRestaurantData } = useMenuContext();
  const { stadiumInfo } = useSystemContext();

  const { t } = useTranslation();
  const [codeObjectId, setCodeObjectId] = useState<string | null>(null);
  const { payment } = usePaymentMethod();
  const location = useLocation();
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [paymentError, setPaymentError] = useState(false);
  const paymentRequestKeyRef = useRef<string | null>(null);
  const queryParams = React.useMemo(() => queryString.parse(location.search), [location.search]);
  const restaurant = restaurant_id ? listRestaurantData.find((restaurant) => restaurant.id === restaurant_id) : null;

  const paymentLocationId = restaurant?.location_id ?? stadiumInfo?.location_id ?? '';
  const paymentMerchantId = restaurant?.merchant_id ?? stadiumInfo?.merchant_id ?? '';

  const sanitizeForLog = useCallback((value: any) => {
    if (value === undefined) return null;
    return JSON.parse(JSON.stringify(value, (_key, item) => (item === undefined ? null : item)));
  }, []);

  const getOrderPaymentLogSnapshot = useCallback(
    async (id: string) => {
      const orderDoc = await db.collection(db_config.DB_TABLE_ALL_ORDERS).doc(id).get({ source: 'server' });
      const data = orderDoc.exists ? orderDoc.data() : null;
      const foods = Array.isArray(data?.foods) ? data?.foods : [];
      const promotionFoods = Array.isArray(data?.promotion_foods) ? data?.promotion_foods : [];
      const orderItemsTotal = calculateOrderItemsTotal({ foods, promotion_foods: promotionFoods });
      const totalItem = calculateOrderItemQuantity({ foods, promotion_foods: promotionFoods });
      const shippingFee = data?.order_status === OrderItemStatus.CANCELLED ? 0 : totalItem === 0 ? 0 : SHIPPING_FEE;
      const totalPriceFromDb = orderItemsTotal + shippingFee;

      return sanitizeForLog({
        exists: orderDoc.exists,
        order_id: id,
        restaurant_id: data?.restaurant_id || null,
        restaurant_ids: data?.restaurant_ids || [],
        bill_status: data?.bill_status || null,
        payment_method: data?.payment_method || null,
        delivery_request: data?.delivery_request || null,
        is_pre_order: data?.is_pre_order || false,
        order_type: data?.order_type || null,
        foods,
        promotion_foods: promotionFoods,
        foods_subtotal: orderItemsTotal,
        shipping_fee: shippingFee,
        total_price_from_db: totalPriceFromDb,
        total_price_to_pay: totalPrice,
      });
    },
    [sanitizeForLog, totalPrice]
  );

  const writePaymentDebugFallbackLog = useCallback(
    async (data: Record<string, any>) => {
      try {
        await db
          .collection(db_config.DB_TABLE_STADIUM_LOGS)
          .doc(PAYMENT_DEBUG_FALLBACK_LOG_ID)
          .set(
            {
              payment_debug_errors: firebase.firestore.FieldValue.arrayUnion(
                sanitizeForLog({
                  ...data,
                  created_at: new Date().toISOString(),
                })
              ),
              payment_debug_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
            },
            { merge: true }
          );
      } catch (error) {
        console.warn('Failed to write fallback payment debug log', error);
      }
    },
    [sanitizeForLog]
  );

  const writePaymentDebugLog = useCallback(
    async (id: string | null | undefined, data: Record<string, any>) => {
      if (!id) {
        await writePaymentDebugFallbackLog({
          reason: 'missing_order_id',
          ...data,
        });
        return;
      }

      try {
        await db
          .collection(db_config.DB_TABLE_STADIUM_LOGS)
          .doc(id)
          .set(
            {
              ...sanitizeForLog(data),
              payment_debug_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
            },
            { merge: true }
          );
      } catch (error) {
        console.warn('Failed to write payment debug log', error);
        await writePaymentDebugFallbackLog({
          reason: 'order_log_write_failed',
          order_id: id,
          error_message: error instanceof Error ? error.message : String(error),
          data,
        });
      }
    },
    [sanitizeForLog, writePaymentDebugFallbackLog]
  );

  useEffect(() => {
    if (open) return;
    paymentRequestKeyRef.current = null;
    setCodeObjectId(null);
    setHasFetched(false);
    setPaymentError(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleBeforeUnload = () => {
      const id = orderId ?? sessionStorage.getItem('orderId');
      if (id) {
        const orderDetailLink = queryString.stringifyUrl({
          url: `/orderdetail/${id}`,
          query: queryParams,
        });
        window.location.href = orderDetailLink;
      }
    };

    const id = orderId ?? sessionStorage.getItem('orderId');
    const paymentRequestKey = `${id || 'missing_order'}:${totalPrice}:${paymentLocationId}:${paymentMerchantId}`;

    const fetchPaymentCode = async () => {
      try {
        if (!id) {
          await writePaymentDebugLog(null, {
            payment_total_price: totalPrice,
            payment_location_id: paymentLocationId,
            payment_merchant_id: paymentMerchantId,
            order_details_foods: orderDetails?.foods || [],
            order_details_promotion_foods: orderDetails?.promotion_foods || [],
          });
          setCodeObjectId(null);
          setHasFetched(true);
          setPaymentError(true);
          return;
        }

        let beforePaymentSnapshot = null;
        try {
          beforePaymentSnapshot = await getOrderPaymentLogSnapshot(id);
        } catch (error) {
          await writePaymentDebugFallbackLog({
            reason: 'before_payment_snapshot_failed',
            order_id: id,
            error_message: error instanceof Error ? error.message : String(error),
            payment_total_price: totalPrice,
            order_details_foods: orderDetails?.foods || [],
            order_details_promotion_foods: orderDetails?.promotion_foods || [],
          });
        }

        await writePaymentDebugLog(id, {
          payment_before_third_party: beforePaymentSnapshot,
          payment_total_price: totalPrice,
          payment_location_id: paymentLocationId,
          payment_merchant_id: paymentMerchantId,
          payment_debug_created_at: new Date().toISOString(),
        });

        if (!beforePaymentSnapshot?.exists) {
          await writePaymentDebugLog(id, {
            payment_blocked_reason: 'order_not_created',
            payment_total_price: totalPrice,
            payment_location_id: paymentLocationId,
            payment_merchant_id: paymentMerchantId,
          });
          setCodeObjectId(null);
          setHasFetched(true);
          setPaymentError(true);
          return;
        }

        const totalPriceFromDb = Number(beforePaymentSnapshot.total_price_from_db || 0);
        if (totalPriceFromDb !== totalPrice) {
          await writePaymentDebugLog(id, {
            payment_blocked_reason: 'amount_mismatch',
            payment_total_price_from_ui: totalPrice,
            payment_total_price_from_db: totalPriceFromDb,
            payment_location_id: paymentLocationId,
            payment_merchant_id: paymentMerchantId,
          });
          setCodeObjectId(null);
          setHasFetched(true);
          setPaymentError(true);
          return;
        }

        const code = await payment(totalPriceFromDb, id, paymentLocationId, paymentMerchantId);
        let afterPaymentSnapshot = null;
        try {
          afterPaymentSnapshot = await getOrderPaymentLogSnapshot(id);
        } catch (error) {
          await writePaymentDebugFallbackLog({
            reason: 'after_payment_snapshot_failed',
            order_id: id,
            error_message: error instanceof Error ? error.message : String(error),
            payment_total_price: totalPrice,
            payment_request_payload: code?.payment_request_payload || null,
          });
        }

        await writePaymentDebugLog(id, {
          payment_after_third_party: afterPaymentSnapshot,
          payment_request_payload: code?.payment_request_payload || null,
          payment_response_summary: {
            id: code?.id || null,
            status: code?.status || null,
            object: code?.object || null,
            orderNo: code?.orderNo || code?.payment_request_payload?.orderNo || null,
          },
          payment_debug_finished_at: new Date().toISOString(),
        });

        if (code?.id) {
          setPaymentError(false);
          setCodeObjectId(code.id);
          setHasFetched(true);
        } else {
          setCodeObjectId(null);
          setHasFetched(true);
          setPaymentError(true);
        }
      } catch (error) {
        setCodeObjectId(null);
        setHasFetched(true);
        setPaymentError(true);
      }
    };

    if (!hasFetched && !paymentError && paymentRequestKeyRef.current !== paymentRequestKey) {
      paymentRequestKeyRef.current = paymentRequestKey;
      fetchPaymentCode();
    }

    window.addEventListener('visibilitychange', handleBeforeUnload);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('visibilitychange', handleBeforeUnload);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    open,
    orderDetails,
    hasFetched,
    paymentError,
    orderId,
    payment,
    totalPrice,
    paymentLocationId,
    paymentMerchantId,
    queryParams,
    getOrderPaymentLogSnapshot,
    writePaymentDebugFallbackLog,
    writePaymentDebugLog,
  ]);
  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '12px',
          margin: '16px',
        },
      }}
    >
      <div className="bg-white rounded-xl p-6 min-w-[400px] max-w-[480px] relative">
        <div className="flex items-start justify-between mb-6">
          <h2 className="text-[#101828] text-lg font-semibold leading-7">{t('REDIRECT_TO_SMCC')}</h2>
          <IconButton
            onClick={onClose}
            sx={{
              padding: '4px',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon sx={{ fontSize: '20px', color: '#667085' }} />
          </IconButton>
        </div>
        {paymentError ? (
          <div className="flex flex-col items-center gap-6">
            <p className="text-[#475467] text-base font-normal text-center">{t('PLEASE_TRY_AGAIN')}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6">
            <div className="w-16 h-16 relative">
              <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
              <div className="absolute inset-0 rounded-full border-4 border-t-blue-500 border-r-blue-400 border-b-blue-300 border-l-blue-200 animate-spin"></div>
            </div>
            <p className="text-[#475467] text-base font-normal">{t('LOADING')}</p>
            <p className="text-[#667085] text-sm font-normal text-center max-w-md">{t('LOADING_PAYMENT_DESCRIPTION')}</p>
          </div>
        )}
        {!paymentError && codeObjectId && <ElepayCheckout codeObjectId={codeObjectId} publicKey={stadiumInfo?.elepay_public_key} />}
      </div>
    </Dialog>
  );
};

export default PAYMENT;
